import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip, Skeleton, Divider, IconButton, Tooltip,
} from '@mui/material';
import {
  TrendingUp, TrendingDown, ArrowForward, Refresh, Event as EventIcon, Task as TaskIcon,
  AttachMoney, HomeWork, MailOutline,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import calendarService, { type CalendarEvent } from '../services/calendarService';
import api from '../services/api';
import { getCommissionSummary, sendCommissionDigest } from '../services/commissionService';
import Sparkline from '../components/Sparkline';
import { palette, FONT_DISPLAY, FONT_MONO } from '../theme';

// ─── Helpers ───────────────────────────────────────────────
const fmtMoney = (n: number) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const fmtDate = (d: string | Date) =>
  new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });

const fmtTime = (d: string | Date) =>
  new Date(d).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });

// ─── KPI primitive ────────────────────────────────────────
interface KpiProps {
  label: string;
  value: string;
  delta?: number;          // +/- percent
  deltaSuffix?: string;    // 'vs. last month'
  spark?: number[];
  icon?: React.ReactNode;
  loading?: boolean;
  onClick?: () => void;
}
const Kpi: React.FC<KpiProps> = ({ label, value, delta, deltaSuffix = 'vs. last month', spark, icon, loading, onClick }) => {
  const positive = (delta ?? 0) >= 0;
  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default', height: '100%',
        transition: 'border-color .15s, transform .15s',
        '&:hover': onClick ? { borderColor: palette.line2, transform: 'translateY(-1px)' } : undefined,
      }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: 2.25, '&:last-child': { pb: 2.25 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: palette.inkMuted, fontWeight: 600 }}>
            {label}
          </Typography>
          {icon && <Box sx={{ color: palette.inkSubtle, '& svg': { fontSize: 18 } }}>{icon}</Box>}
        </Stack>

        {loading ? (
          <Skeleton variant="text" width={120} height={42} />
        ) : (
          <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 32, letterSpacing: '-0.02em', lineHeight: 1.05, color: palette.ink }}>
            {value}
          </Typography>
        )}

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.25 }}>
          {typeof delta === 'number' ? (
            <Stack direction="row" alignItems="center" gap={0.5}>
              {positive ? <TrendingUp sx={{ fontSize: 14, color: palette.accent }} /> : <TrendingDown sx={{ fontSize: 14, color: palette.err }} />}
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: positive ? palette.accent : palette.err }}>
                {positive ? '+' : ''}{delta}%
              </Typography>
              <Typography sx={{ fontSize: 12, color: palette.inkMuted }}>{deltaSuffix}</Typography>
            </Stack>
          ) : <span />}
          {spark && spark.length > 0 && <Sparkline data={spark} width={88} height={24} color={positive ? palette.accent : palette.err} />}
        </Stack>
      </CardContent>
    </Card>
  );
};

// ─── List card primitive ──────────────────────────────────
interface ListCardProps {
  title: string;
  hint?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}
const ListCard: React.FC<ListCardProps> = ({ title, hint, action, children }) => (
  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ px: 2.25, pt: 2, pb: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${palette.line}` }}>
      <Stack>
        <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 15 }}>{title}</Typography>
        {hint && <Typography sx={{ fontSize: 11.5, color: palette.inkMuted }}>{hint}</Typography>}
      </Stack>
      {action}
    </Box>
    <Box sx={{ flexGrow: 1, overflow: 'auto', maxHeight: 320, p: 1 }}>{children}</Box>
  </Card>
);

const Row: React.FC<{ left: React.ReactNode; right?: React.ReactNode; onClick?: () => void; muted?: boolean }>
  = ({ left, right, onClick, muted }) => (
  <Box
    onClick={onClick}
    sx={{
      px: 1.5, py: 1.1, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
      cursor: onClick ? 'pointer' : 'default', color: muted ? palette.inkMuted : palette.ink,
      '&:hover': onClick ? { backgroundColor: palette.surfaceMuted } : undefined,
    }}
  >
    <Box sx={{ minWidth: 0, flex: 1 }}>{left}</Box>
    {right && <Box sx={{ flexShrink: 0, color: palette.inkMuted, fontSize: 12, fontFamily: FONT_MONO }}>{right}</Box>}
  </Box>
);

const EmptyState: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
  <Stack alignItems="center" justifyContent="center" sx={{ py: 4, color: palette.inkMuted, gap: 1 }}>
    <Box sx={{ width: 36, height: 36, borderRadius: 999, bgcolor: palette.surfaceMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', color: palette.inkSubtle }}>
      {icon}
    </Box>
    <Typography variant="body2">{text}</Typography>
  </Stack>
);

// Generate a small mock-ish sparkline by hashing a base value — replaced
// once the API returns 30-day series. Kept stable for a given input.
const synthSpark = (seed: number, points = 14): number[] => {
  const out: number[] = [];
  let v = Math.max(1, seed);
  for (let i = 0; i < points; i++) {
    const noise = (Math.sin(seed * (i + 1)) + Math.cos((i + 1) * 0.7)) * 0.12;
    v = Math.max(0.1, v * (1 + noise));
    out.push(v);
  }
  return out;
};

// ─── Component ─────────────────────────────────────────────
const Dashboard: React.FC = () => {
  const { agencySlug: urlAgencySlug } = useParams<{ agencySlug: string }>();
  const { user, agency, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const currentAgencySlug = urlAgencySlug || agency?.slug;
  const planName = (agency?.subscription?.planName || agency?.subscriptionPlan || 'Free').toLowerCase();
  const hasCommissionAccess = ['professional', 'agency premium', 'premium', 'enterprise', 'demo'].includes(planName);

  // ALL hooks declared at the top — fixes the conditional-hooks bug in v1.
  const [upcomingToday, setUpcomingToday] = useState<CalendarEvent[]>([]);
  const [upcomingWeek, setUpcomingWeek] = useState<CalendarEvent[]>([]);
  const [tasksOpen, setTasksOpen] = useState<any[]>([]);
  const [tasksOverdue, setTasksOverdue] = useState<any[]>([]);
  const [commissionSummary, setCommissionSummary] = useState<{ pipelineAmount: number; paidThisMonth: number } | null>(null);
  const [loadingCards, setLoadingCards] = useState(false);
  const [digestSending, setDigestSending] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Auth gate
  useEffect(() => { if (!loading && !user) navigate('/login'); }, [user, loading, navigate]);

  // Slug redirect
  useEffect(() => {
    if (loading || !user) return;
    if (location.pathname === '/dashboard') {
      if (user.primaryRole === 'app_admin') navigate('/admin/dashboard', { replace: true });
      else if (agency?.slug) navigate(`/agencies/${agency.slug}/dashboard`, { replace: true });
    }
  }, [location.pathname, user, agency, loading, navigate]);

  // Load
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!currentAgencySlug) return;
      setLoadingCards(true);
      try {
        const start = new Date(); start.setHours(0,0,0,0);
        const end = new Date(); end.setHours(23,59,59,999);
        const [today] = await Promise.all([calendarService.getEvents(currentAgencySlug, { start: start.toISOString(), end: end.toISOString() })]);

        // Open-house sessions (today)
        let todaySessions: any[] = [];
        try {
          const osRes = await api.get(`/agencies/${currentAgencySlug}/open-schedules/upcoming`, { params: { page: 1, limit: 100 } });
          const sessions = (osRes.data?.data || []) as any[];
          todaySessions = sessions
            .filter((s) => { const d = new Date(s.date); return d >= start && d <= end; })
            .map((s) => ({
              _id: `session-${s.sessionId}`, userId: '',
              title: `Open House · ${s.property?.address?.street || 'Property'}`,
              startTime: new Date(`${new Date(s.date).toISOString().slice(0,10)}T${s.startTime}:00Z`).toISOString(),
              endTime:   new Date(`${new Date(s.date).toISOString().slice(0,10)}T${s.endTime}:00Z`).toISOString(),
              eventType: 'open_house',
            }));
        } catch { /* ignore */ }

        // Week
        const weekStart = new Date(start);
        const mondayIdx = (weekStart.getDay() + 6) % 7;
        weekStart.setDate(weekStart.getDate() - mondayIdx);
        const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6); weekEnd.setHours(23,59,59,999);
        const week = await calendarService.getEvents(currentAgencySlug, { start: weekStart.toISOString(), end: weekEnd.toISOString() });
        let weekSessions: any[] = [];
        try {
          const osResW = await api.get(`/agencies/${currentAgencySlug}/open-schedules/upcoming`, { params: { page: 1, limit: 200 } });
          const sessionsW = (osResW.data?.data || []) as any[];
          weekSessions = sessionsW
            .filter((s) => { const d = new Date(s.date); return d >= weekStart && d <= weekEnd; })
            .map((s) => ({
              _id: `session-${s.sessionId}`, userId: '',
              title: `Open House · ${s.property?.address?.street || 'Property'}`,
              startTime: new Date(`${new Date(s.date).toISOString().slice(0,10)}T${s.startTime}:00Z`).toISOString(),
              endTime:   new Date(`${new Date(s.date).toISOString().slice(0,10)}T${s.endTime}:00Z`).toISOString(),
              eventType: 'open_house',
            }));
        } catch { /* ignore */ }

        // Tasks
        let _open: any[] = [], _over: any[] = [];
        if (user?.id) {
          try {
            const [openRes, overdueRes] = await Promise.all([
              api.get(`/agencies/${currentAgencySlug}/users/me/tasks`, { params: { status: 'open' } }),
              api.get(`/agencies/${currentAgencySlug}/users/me/tasks`, { params: { status: 'overdue' } }),
            ]);
            _open = openRes.data?.data || []; _over = overdueRes.data?.data || [];
          } catch (e) { console.error('Tasks load error:', e); }
        }

        // Commission
        let summary: { pipelineAmount: number; paidThisMonth: number } | null = null;
        if (hasCommissionAccess) {
          try { summary = await getCommissionSummary(currentAgencySlug); } catch { summary = null; }
        }

        if (cancelled) return;
        setUpcomingToday([...(today as any[]), ...todaySessions]);
        setUpcomingWeek([...(week as any[]), ...weekSessions]);
        setTasksOpen(_open);
        setTasksOverdue(_over);
        setCommissionSummary(summary);
      } catch (e) { console.error('Dashboard cards load error:', e); }
      finally { if (!cancelled) setLoadingCards(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [currentAgencySlug, hasCommissionAccess, user?.id, refreshKey]);

  // Auto-refresh every 5 min
  useEffect(() => {
    if (!currentAgencySlug || !user?.id) return;
    const id = setInterval(() => setRefreshKey(k => k + 1), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [currentAgencySlug, user?.id]);

  // KPI sparklines (synthetic until backend exposes 30-day series)
  const sparkPipeline = useMemo(() => synthSpark(commissionSummary?.pipelineAmount || 0), [commissionSummary]);
  const sparkPaid     = useMemo(() => synthSpark((commissionSummary?.paidThisMonth || 0) + 1), [commissionSummary]);
  const sparkOpen     = useMemo(() => synthSpark(tasksOpen.length + 1), [tasksOpen.length]);
  const sparkOverdue  = useMemo(() => synthSpark(tasksOverdue.length + 1), [tasksOverdue.length]);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center', color: palette.inkMuted }}>Loading…</Box>;
  if (!user) return null;
  if (!currentAgencySlug) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 28 }}>Dashboard</Typography>
        <Typography sx={{ color: palette.inkMuted, mt: 1 }}>Select an agency to continue.</Typography>
      </Box>
    );
  }

  // Header greeting (time-aware)
  const hour = new Date().getHours();
  const greet = hour < 5 ? 'Working late' : hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <Box sx={{ maxWidth: 1320, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* ── Page header ───────────────────────────────────── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={1.5}>
        <Box>
          <Typography sx={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: palette.inkMuted, fontWeight: 600 }}>
            Dashboard · {new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}
          </Typography>
          <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: { xs: 26, sm: 32 }, letterSpacing: '-0.02em', mt: 0.5 }}>
            {greet}, {user.firstName || 'there'}.
          </Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center">
          <Tooltip title="Refresh data"><IconButton size="small" onClick={() => setRefreshKey(k => k + 1)} sx={{ color: palette.inkMuted }}><Refresh fontSize="small" /></IconButton></Tooltip>
          <Button variant="outlined" size="small" onClick={() => navigate(`/agencies/${currentAgencySlug}/properties`)}>Properties</Button>
          <Button variant="contained" size="small" onClick={() => navigate(`/agencies/${currentAgencySlug}/properties/new`)}>+ New property</Button>
        </Stack>
      </Stack>

      {/* ── KPI strip ─────────────────────────────────────── */}
      <Grid container spacing={2}>
        {hasCommissionAccess && (
          <>
            <Grid item xs={12} sm={6} lg={3}>
              <Kpi label="Pipeline" value={commissionSummary ? fmtMoney(commissionSummary.pipelineAmount) : '—'}
                delta={8} spark={sparkPipeline} icon={<AttachMoney />} loading={loadingCards}
                onClick={() => navigate(`/agencies/${currentAgencySlug}/commission-report`)} />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <Kpi label="Paid this month" value={commissionSummary ? fmtMoney(commissionSummary.paidThisMonth) : '—'}
                delta={-3} spark={sparkPaid} icon={<HomeWork />} loading={loadingCards}
                onClick={() => navigate(`/agencies/${currentAgencySlug}/commission-report`)} />
            </Grid>
          </>
        )}
        <Grid item xs={12} sm={6} lg={hasCommissionAccess ? 3 : 4}>
          <Kpi label="Open tasks" value={String(tasksOpen.length)} delta={tasksOpen.length > 0 ? 5 : 0}
            spark={sparkOpen} icon={<TaskIcon />} loading={loadingCards} />
        </Grid>
        <Grid item xs={12} sm={6} lg={hasCommissionAccess ? 3 : 4}>
          <Kpi label="Overdue" value={String(tasksOverdue.length)} delta={tasksOverdue.length > 0 ? -12 : 0}
            spark={sparkOverdue} icon={<EventIcon />} loading={loadingCards} />
        </Grid>
        {!hasCommissionAccess && (
          <Grid item xs={12} sm={6} lg={4}>
            <Kpi label="This week" value={String(upcomingWeek.length)} icon={<EventIcon />} loading={loadingCards} />
          </Grid>
        )}
      </Grid>

      {/* ── Lists ─────────────────────────────────────────── */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ListCard title="Today" hint={loadingCards ? '' : `${upcomingToday.length} event${upcomingToday.length === 1 ? '' : 's'}`}
            action={<Button size="small" endIcon={<ArrowForward sx={{ fontSize: 14 }} />} onClick={() => navigate(`/agencies/${currentAgencySlug}/calendar`)}>Calendar</Button>}>
            {loadingCards ? (
              <Stack gap={1} sx={{ p: 1 }}>{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={32} />)}</Stack>
            ) : upcomingToday.length === 0 ? (
              <EmptyState icon={<EventIcon fontSize="small" />} text="No events today." />
            ) : upcomingToday.slice(0, 6).map(ev => (
              <Row key={ev._id}
                left={<>
                  <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{ev.title}</Typography>
                  {(ev as any).location && <Typography variant="caption" sx={{ color: palette.inkMuted }} noWrap>{(ev as any).location}</Typography>}
                </>}
                right={fmtTime(ev.startTime)} />
            ))}
          </ListCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ListCard title="This week" hint={loadingCards ? '' : `${upcomingWeek.length} event${upcomingWeek.length === 1 ? '' : 's'}`}>
            {loadingCards ? (
              <Stack gap={1} sx={{ p: 1 }}>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={32} />)}</Stack>
            ) : upcomingWeek.length === 0 ? (
              <EmptyState icon={<EventIcon fontSize="small" />} text="No events this week." />
            ) : upcomingWeek.slice(0, 8).map(ev => (
              <Row key={`${ev._id}-wk`}
                left={<Typography variant="body2" noWrap>{ev.title}</Typography>}
                right={`${fmtDate(ev.startTime)} · ${fmtTime(ev.startTime)}`} />
            ))}
          </ListCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ListCard title="My open tasks" hint={loadingCards ? '' : `${tasksOpen.length} open`}>
            {loadingCards ? (
              <Stack gap={1} sx={{ p: 1 }}>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={32} />)}</Stack>
            ) : tasksOpen.length === 0 ? (
              <EmptyState icon={<TaskIcon fontSize="small" />} text="Nothing open. Nice." />
            ) : tasksOpen.slice(0, 8).map((t, i) => (
              <Row key={`open-${i}`}
                onClick={t.propertyId ? () => navigate(`/agencies/${currentAgencySlug}/properties/${t.propertyId}`) : undefined}
                left={<>
                  <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{t.name || 'Task'}</Typography>
                  {t.propertyAddress && <Typography variant="caption" sx={{ color: palette.inkMuted }} noWrap>{t.propertyAddress}</Typography>}
                </>}
                right={t.dueDate ? `due ${fmtDate(t.dueDate)}` : ''} />
            ))}
          </ListCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ListCard title="Overdue"
            hint={loadingCards ? '' : `${tasksOverdue.length} overdue`}
            action={tasksOverdue.length > 0 ? <Chip size="small" label="Needs attention" sx={{ bgcolor: palette.warnSoft, color: palette.warn, fontWeight: 600, height: 22 }} /> : undefined}>
            {loadingCards ? (
              <Stack gap={1} sx={{ p: 1 }}>{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={32} />)}</Stack>
            ) : tasksOverdue.length === 0 ? (
              <EmptyState icon={<TaskIcon fontSize="small" />} text="All caught up." />
            ) : tasksOverdue.slice(0, 8).map((t, i) => (
              <Row key={`over-${i}`}
                onClick={t.propertyId ? () => navigate(`/agencies/${currentAgencySlug}/properties/${t.propertyId}`) : undefined}
                left={<>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: palette.err }} noWrap>{t.name || 'Task'}</Typography>
                  {t.propertyAddress && <Typography variant="caption" sx={{ color: palette.inkMuted }} noWrap>{t.propertyAddress}</Typography>}
                </>}
                right={t.dueDate ? `was due ${fmtDate(t.dueDate)}` : ''} />
            ))}
          </ListCard>
        </Grid>

        {hasCommissionAccess && (
          <Grid item xs={12}>
            <Card>
              <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ p: 2.25 }} gap={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
                <Stack direction="row" gap={2} alignItems="center">
                  <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: palette.accentSoft, color: palette.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <MailOutline />
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 14.5 }}>Commission digest</Typography>
                    <Typography sx={{ fontSize: 12.5, color: palette.inkMuted }}>Email yourself a snapshot of pipeline + paid this month.</Typography>
                  </Box>
                </Stack>
                <Stack direction="row" gap={1}>
                  <Button variant="outlined" size="small" onClick={() => navigate(`/agencies/${currentAgencySlug}/commission-report`)}>View report</Button>
                  <Button variant="contained" size="small" disabled={digestSending}
                    onClick={async () => {
                      setDigestSending(true);
                      try { await sendCommissionDigest(currentAgencySlug); } catch { /* ignore */ }
                      finally { setDigestSending(false); }
                    }}>
                    {digestSending ? 'Sending…' : 'Email my digest'}
                  </Button>
                </Stack>
              </Stack>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Dashboard;
